import { Head, Link } from "@inertiajs/react"
import {
  FileText,
  Clock,
  CheckCircle,
  AlertCircle,
  Users,
  ArrowRight,
} from "lucide-react"
import { AdminLayout } from "./components/AdminLayout"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"

const statusConfig = {
  draft: { label: "Draft", variant: "secondary", icon: FileText },
  pending_review: { label: "Pending", variant: "warning", icon: Clock },
  published: { label: "Published", variant: "success", icon: CheckCircle },
  rejected: { label: "Rejected", variant: "destructive", icon: AlertCircle },
}

function StatusBadge({ status }) {
  const config = statusConfig[status] || statusConfig.draft
  return (
    <Badge
      variant={config.variant}
      className={
        config.variant === "warning"
          ? "bg-amber-500 text-white hover:bg-amber-500"
          : config.variant === "success"
          ? "bg-green-500 text-white hover:bg-green-500"
          : ""
      }
    >
      {config.label}
    </Badge>
  )
}

function formatDate(dateString) {
  if (!dateString) return "-"
  return new Date(dateString).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  })
}

export default function Dashboard({
  currentUser,
  stats,
  pendingArticles,
  recentArticles,
}) {
  return (
    <AdminLayout currentUser={currentUser} pendingCount={stats?.pendingReview}>
      <Head title="Admin Dashboard - Kingdom Factor" />

      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-zinc-900">Admin Dashboard</h1>
          <p className="text-zinc-500">Overview of article management</p>
        </div>

        {/* Stats Grid */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          <Card className="bg-white border-zinc-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-zinc-500">
                Total Articles
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-zinc-900">
                {stats?.totalArticles || 0}
              </div>
            </CardContent>
          </Card>
          <Card className="bg-amber-50 border-amber-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-amber-700">
                Pending Review
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-amber-700">
                {stats?.pendingReview || 0}
              </div>
            </CardContent>
          </Card>
          <Card className="bg-green-50 border-green-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-green-700">
                Published
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-700">
                {stats?.published || 0}
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border-zinc-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-zinc-500">
                Drafts
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-zinc-700">
                {stats?.drafts || 0}
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border-zinc-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-zinc-500">
                Authors
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-zinc-900">
                {stats?.totalAuthors || 0}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Pending Review */}
        {pendingArticles?.length > 0 && (
          <Card className="bg-amber-50 border-amber-200">
            <CardHeader className="flex flex-row items-center justify-between">
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-amber-600" />
                <CardTitle className="text-amber-900">
                  Pending Review ({pendingArticles.length})
                </CardTitle>
              </div>
              <Link href="/app/admin/articles/pending_review">
                <Button variant="ghost" size="sm" className="text-amber-700">
                  View All
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              <div className="divide-y divide-amber-200">
                {pendingArticles.map((article) => (
                  <Link
                    key={article.id}
                    href={`/app/admin/articles/${article.id}`}
                    className="flex items-center justify-between py-3 hover:bg-amber-100/50 -mx-4 px-4 transition-colors first:-mt-3 last:-mb-3"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-amber-900 truncate">
                        {article.title}
                      </p>
                      <p className="text-sm text-amber-600">
                        by {article.author?.name || article.author?.email}{" "}
                        &middot; Submitted {formatDate(article.submittedAt)}
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-amber-300 text-amber-700 hover:bg-amber-100"
                    >
                      Review
                    </Button>
                  </Link>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Recent Articles */}
        <Card className="bg-white border-zinc-200">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-zinc-900">Recent Activity</CardTitle>
            <Link href="/app/admin/articles">
              <Button variant="ghost" size="sm" className="text-zinc-500">
                View All
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {recentArticles?.length > 0 ? (
              <div className="divide-y divide-zinc-100">
                {recentArticles.map((article) => (
                  <Link
                    key={article.id}
                    href={`/app/admin/articles/${article.id}`}
                    className="flex items-center justify-between py-3 hover:bg-zinc-50 -mx-4 px-4 transition-colors"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-zinc-900 truncate">
                        {article.title}
                      </p>
                      <p className="text-sm text-zinc-500">
                        {article.author?.name || article.author?.email} &middot;{" "}
                        {article.category || "No category"} &middot; Updated{" "}
                        {formatDate(article.updatedAt)}
                      </p>
                    </div>
                    <StatusBadge status={article.status} />
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-zinc-400">
                <FileText className="h-12 w-12 mx-auto mb-3 text-zinc-300" />
                <p>No articles yet</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  )
}
